import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, GalleryHorizontal, MessageSquareText, X } from 'lucide-react'

import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from 'components/ui/carousel'
import useMediaQuery from 'hooks/useMediaQuery'
import { cn } from 'lib/utils'

import { BlockRenderModeProvider } from '../../blocks/renderMode'
import type { AssetName } from '../../hooks/useAssetNames'
import type { BlockView } from '../../hooks/useResolvedBlocks'
import { transitionFor, type PresentedSlide, type StorySpan, type UsePresenter } from '../../hooks/usePresenter'
import type { EntityRef, Transition } from '../../types/presentation.types'
import { PageFlip, TIMINGS } from './PageFlip'
import { PresentedSlideView } from './PresentedSlideView'
import { SpeakerNotesOverlay } from './SpeakerNotesOverlay'
import { StoryMenu } from './StoryMenu'

/**
 * Presenting from a tablet (MYD-498). Phones are deliberately not served:
 * see `utils/viewerKind.ts`.
 *
 * ## Moving
 *
 * SWIPE follows the finger: the stage is an embla carousel, so a slide can be
 * half-dragged and let go. A TAP plays the deck's own transition (the page
 * turn, by default) like the desktop presenter: right of the first third goes
 * forward, the first third goes back. Embla swallows the click that ends a
 * drag, so a swipe is never also read as a tap.
 *
 * The page turn is drawn as an overlay above the carousel, which meanwhile
 * jumps to the new slide underneath; when the turn ends the overlay goes and
 * the carousel is already there.
 *
 * ## Layout
 *
 * The stage takes all the height there is, and the slide is scaled to fit it.
 * In PORTRAIT the filmstrip sits under the stage, where there is room for it.
 * In LANDSCAPE the stage gets the room and the filmstrip is behind a button.
 *
 * Heights are `100dvh` and the bottom bar is padded by
 * `env(safe-area-inset-bottom)`: on iOS Safari `100vh` reaches under the
 * toolbar and the home indicator, so a bar anchored with it is hidden on real
 * hardware even though it measures correctly in an emulator (CLAUDE.md).
 *
 * ## Cost
 *
 * Only the slide on screen and its neighbours are drawn; the rest are empty
 * frames until approached. A deck of 100 slides would otherwise hold 100 live
 * slides, and a live map each, on an iPad. The filmstrip likewise draws only
 * the thumbnails near the current slide.
 */

/** Slides either side of the current one drawn for real. */
const DRAWN_AROUND = 1
/** Thumbnails either side of the current one drawn in the filmstrip. */
const THUMBS_AROUND = 12
const THUMB_WIDTH = 112
const REDUCED_MOTION_MS = 200

interface TouchViewerProps {
	slides: PresentedSlide[]
	presenter: UsePresenter
	blocks: Record<string, BlockView | undefined>
	theme?: string
	deckTransition: Transition
	storyCount: number
	assetName: (ref: EntityRef | null) => AssetName | null
	stories: StorySpan[]
	currentStory: number
	reducedMotion: boolean
	notice: string | null
	/** False on a shared link (MYD-509): the recipient is not the presenter. */
	showNotes?: boolean
	onExit: () => void
}

interface Flip {
	from: number
	to: number
	serial: number
}

export function TouchViewer({
	slides,
	presenter,
	blocks,
	theme,
	deckTransition,
	storyCount,
	assetName,
	stories,
	currentStory,
	reducedMotion,
	notice,
	showNotes = true,
	onExit,
}: TouchViewerProps) {
	const portrait = useMediaQuery('(orientation: portrait)')
	const [api, setApi] = useState<CarouselApi>()
	const [stageSize, setStageSize] = useState({ width: 0, height: 0 })
	const [filmstripOpen, setFilmstripOpen] = useState(false)
	const [flip, setFlip] = useState<Flip | null>(null)
	const stageRef = useRef<HTMLDivElement>(null)
	const index = presenter.index
	const indexRef = useRef(index)
	indexRef.current = index
	const goRef = useRef(presenter.go)
	goRef.current = presenter.go

	// --- The slide's size: the largest 16:9 frame the stage holds -----------
	useLayoutEffect(() => {
		const stage = stageRef.current
		if (!stage) return
		const measure = () => setStageSize({ width: stage.clientWidth, height: stage.clientHeight })
		measure()
		if (typeof ResizeObserver === 'undefined') return
		const observer = new ResizeObserver(measure)
		observer.observe(stage)
		return () => observer.disconnect()
	}, [])
	const slideWidth = Math.max(0, Math.floor(Math.min(stageSize.width, (stageSize.height * 16) / 9)))
	const slideHeight = Math.floor((slideWidth * 9) / 16)

	// --- Carousel and presenter, kept on the same slide ---------------------
	// A swipe moves the carousel, which tells the presenter (and so the URL,
	// the progress and the next story's resolve).
	useEffect(() => {
		if (!api) return
		const onSelect = () => {
			const selected = api.selectedScrollSnap()
			if (selected !== indexRef.current) goRef.current(selected, selected > indexRef.current ? 'next' : 'previous')
		}
		api.on('select', onSelect)
		return () => {
			api.off('select', onSelect)
		}
	}, [api])

	// Anything else moving the presenter (a tap, the filmstrip, a story, a
	// key, a continuation slide appearing) moves the carousel, without
	// animation: a tap has its own, and a jump should not scroll past ten slides.
	useEffect(() => {
		if (api && api.selectedScrollSnap() !== index) api.scrollTo(index, true)
	}, [api, index, slides.length])

	// --- Tap: the page turn ---------------------------------------------------
	const flipTransition = flip ? transitionFor({ from: flip.from, to: flip.to, kind: flip.to > flip.from ? 'next' : 'previous', serial: flip.serial }, slides, deckTransition) : 'none'
	useEffect(() => {
		if (!flip) return
		const duration = reducedMotion ? REDUCED_MOTION_MS : TIMINGS[flipTransition]
		const timer = setTimeout(() => setFlip(current => (current?.serial === flip.serial ? null : current)), duration + 120)
		return () => clearTimeout(timer)
	}, [flip, flipTransition, reducedMotion])

	const tap = (event: React.MouseEvent<HTMLDivElement>) => {
		if (presenter.storyMenu) return
		const bounds = event.currentTarget.getBoundingClientRect()
		const back = event.clientX - bounds.left < bounds.width / 3
		const to = back ? index - 1 : index + 1
		if (to < 0 || to >= slides.length) return
		const transition = transitionFor({ from: index, to, kind: back ? 'previous' : 'next', serial: 0 }, slides, deckTransition)
		if (transition !== 'none') setFlip(current => ({ from: index, to, serial: (current?.serial ?? 0) + 1 }))
		presenter.go(to, back ? 'previous' : 'next')
	}

	const drawSlide = useCallback(
		(at: number) => {
			const entry = slides[at]
			if (!entry) return null
			return <PresentedSlideView entry={entry} width={slideWidth} blocks={blocks} theme={theme} storyCount={storyCount} assetName={assetName} />
		},
		[slides, slideWidth, blocks, theme, storyCount, assetName],
	)

	const current = slides[index]
	const showFilmstrip = portrait || filmstripOpen

	return (
		<div className="flex h-[100dvh] w-full flex-col bg-black text-white" data-testid="touch-viewer">
			{/* --- The stage ------------------------------------------------ */}
			<div ref={stageRef} className="relative min-h-0 flex-1">
				{slideWidth > 0 && (
					<Carousel setApi={setApi} opts={{ startIndex: index, duration: 22 }} className="h-full" aria-label="Slides">
						<CarouselContent className="ml-0 h-full">
							{slides.map((entry, at) => (
								<CarouselItem key={entry.slide.id} className="flex h-full items-center justify-center pl-0" aria-label={`Slide ${at + 1} of ${slides.length}`}>
									<div onClick={tap} style={{ width: slideWidth, height: slideHeight }} className="relative" data-testid={at === index ? 'touch-current-slide' : undefined}>
										{Math.abs(at - index) <= DRAWN_AROUND ? drawSlide(at) : <div className="h-full w-full bg-neutral-900" />}
									</div>
								</CarouselItem>
							))}
						</CarouselContent>
					</Carousel>
				)}

				{flip && (
					<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center" aria-hidden>
						<div style={{ width: slideWidth, height: slideHeight }}>
							<FlipPlayer
								key={flip.serial}
								from={flip.from}
								to={flip.to}
								transition={flipTransition}
								reducedMotion={reducedMotion}
								render={drawSlide}
							/>
						</div>
					</div>
				)}

				{showNotes && presenter.notes && current && !current.divider && <SpeakerNotesOverlay notes={current.slide.speakerNotes} />}

				{notice && (
					<p className="absolute inset-x-0 top-4 z-20 mx-auto w-fit rounded-full bg-black/80 px-4 py-1.5 text-sm text-white/90" role="status">
						{notice}
					</p>
				)}
			</div>

			{/* --- The bottom bar --------------------------------------------- */}
			<div className="shrink-0 border-t border-white/10 bg-neutral-950 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2">
				{showFilmstrip && (
					<Filmstrip
						slides={slides}
						current={index}
						blocks={blocks}
						theme={theme}
						storyCount={storyCount}
						assetName={assetName}
						onPick={to => {
							presenter.go(to, 'jump')
							if (!portrait) setFilmstripOpen(false)
						}}
					/>
				)}

				<div className="flex items-center gap-2 px-3">
					<ControlButton label="End presentation" onClick={onExit}>
						<X className="h-5 w-5" />
					</ControlButton>

					<button
						type="button"
						onClick={() => presenter.setStoryMenu(!presenter.storyMenu)}
						className="flex h-11 min-w-0 items-center gap-1.5 rounded-full bg-white/10 px-4 text-sm font-medium active:bg-white/20"
						aria-label="Stories"
					>
						<span className="truncate">{stories.find(story => story.storyIndex === currentStory)?.title ?? 'Stories'}</span>
						<ChevronDown className="h-4 w-4 shrink-0 text-white/60" />
					</button>

					<span className="ml-auto text-sm tabular-nums text-white/70" aria-live="polite">
						{index + 1} / {slides.length}
					</span>

					{showNotes && (
						<ControlButton label={presenter.notes ? 'Hide notes' : 'Show notes'} pressed={presenter.notes} onClick={() => presenter.setNotes(!presenter.notes)}>
							<MessageSquareText className="h-5 w-5" />
						</ControlButton>
					)}
					{!portrait && (
						<ControlButton label={filmstripOpen ? 'Hide slides' : 'Show slides'} pressed={filmstripOpen} onClick={() => setFilmstripOpen(open => !open)}>
							<GalleryHorizontal className="h-5 w-5" />
						</ControlButton>
					)}
				</div>
			</div>

			{presenter.storyMenu && (
				<StoryMenu
					stories={stories}
					currentStory={currentStory}
					onPick={start => {
						presenter.go(start, 'jump')
						presenter.setStoryMenu(false)
					}}
					onClose={() => presenter.setStoryMenu(false)}
				/>
			)}
		</div>
	)
}

/** A finger-sized round control: 44px, Apple's minimum touch target. */
function ControlButton({ label, pressed, onClick, children }: { label: string; pressed?: boolean; onClick: () => void; children: ReactNode }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={label}
			aria-pressed={pressed}
			className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white/80 active:bg-white/20', pressed && 'bg-white/15 text-white')}
		>
			{children}
		</button>
	)
}

/** Plays one page turn, from one slide to another, then holds the second. */
function FlipPlayer({
	from,
	to,
	transition,
	reducedMotion,
	render,
}: {
	from: number
	to: number
	transition: Transition
	reducedMotion: boolean
	render: (at: number) => ReactNode
}) {
	const [shown, setShown] = useState(from)
	// Mounted showing the slide being left, then switched: the switch is what
	// PageFlip animates.
	useEffect(() => {
		const frame = requestAnimationFrame(() => setShown(to))
		return () => cancelAnimationFrame(frame)
	}, [to])
	return (
		<PageFlip slideKey={String(shown)} transition={transition} direction={to > from ? 1 : -1} reducedMotion={reducedMotion}>
			{render(shown)}
		</PageFlip>
	)
}

/** Every slide as a thumbnail in a row; the current one is kept in view. */
function Filmstrip({
	slides,
	current,
	blocks,
	theme,
	storyCount,
	assetName,
	onPick,
}: {
	slides: PresentedSlide[]
	current: number
	blocks: Record<string, BlockView | undefined>
	theme?: string
	storyCount: number
	assetName: (ref: EntityRef | null) => AssetName | null
	onPick: (index: number) => void
}) {
	const listRef = useRef<HTMLOListElement>(null)
	useEffect(() => {
		const item = listRef.current?.children[current] as HTMLElement | undefined
		item?.scrollIntoView?.({ block: 'nearest', inline: 'center', behavior: 'smooth' })
	}, [current])

	return (
		<ol ref={listRef} className="mb-2 flex gap-2 overflow-x-auto px-3 pb-1 [scrollbar-width:none]" aria-label="All slides">
			{slides.map((entry, at) => {
				const label = entry.divider ? 'Story divider' : entry.slide.title
				return (
					<li key={entry.slide.id} className="shrink-0">
						<button
							type="button"
							onClick={() => onPick(at)}
							aria-label={`Go to slide ${at + 1}${label ? `: ${label}` : ''}`}
							aria-current={at === current ? 'true' : undefined}
							className={cn('block rounded-md p-0.5', at === current ? 'bg-primary' : 'bg-transparent')}
						>
							{Math.abs(at - current) <= THUMBS_AROUND ? (
								// A live map per thumbnail would exhaust the page's WebGL contexts.
								<BlockRenderModeProvider value="thumbnail">
									<PresentedSlideView
										entry={entry}
										width={THUMB_WIDTH}
										blocks={blocks}
										theme={theme}
										storyCount={storyCount}
										assetName={assetName}
										className="pointer-events-none overflow-hidden rounded-sm"
									/>
								</BlockRenderModeProvider>
							) : (
								<span className="flex items-center justify-center rounded-sm bg-neutral-800 text-xs text-white/50" style={{ width: THUMB_WIDTH, height: (THUMB_WIDTH * 9) / 16 }}>
									{at + 1}
								</span>
							)}
						</button>
					</li>
				)
			})}
		</ol>
	)
}

export default TouchViewer
